import { useTranslation } from 'react-i18next'
import {
    formatTimeSelection,
    getDefaultStartTimeSelection,
    getOrdinalSuffix,
    parseTimeSelection,
    RepeatRule,
    TimeSelection,
    toRRule,
} from './ScheduleSessionLogic'
import { Dispatch, SetStateAction, useContext, useEffect, useRef, useState } from 'react'
import _ from 'lodash'
import { RRule } from 'rrule'
import moment from 'moment'
import MDatePicker from '../../UIComponents/MDatePicker'
import { UserContext, UserContextType } from '../../UserContext'
import { SimpleSelect } from '@/UIComponents2/Select/SimpleSelect'
import Tooltip from '@/UIComponents2/Tooltip'
import { TextInput } from '@/UIComponents2/TextInput'
import { cn } from '@/utlis/cn'
import { Checkbox } from '@/UIComponents2/Checkbox'

export default function ScheduleSessionRecurrenceSection({
    startTimeSelection,
    startDate,
    repeatRule,
    timeZoneCode,
    isModify,
    calendarCloserClickEv,
    setRepeatRule,
}: {
    startTimeSelection: TimeSelection | null
    startDate: Date | null
    repeatRule: RepeatRule
    timeZoneCode: string
    isModify: boolean
    calendarCloserClickEv?: React.MouseEvent<HTMLDivElement, MouseEvent>
    setRepeatRule: Dispatch<SetStateAction<RepeatRule>>
}) {
    const { t } = useTranslation()

    const { state } = useContext(UserContext) as UserContextType

    const startDateWeekday = (startDate || new Date()).toLocaleDateString('en-US', { weekday: 'long' })
    const startDateDay =
        '' + (startDate || new Date()).getDate() + getOrdinalSuffix((startDate || new Date()).getDate())

    const customRepeatSelection: string = repeatRule.freq || 'daily'

    const datePickerContainerRef = useRef<HTMLDivElement>(null)

    const [rrule, setRrule] = useState<RRule | null>(null)

    useEffect(() => {
        const next = structuredClone(repeatRule)

        const { hour, min } = parseTimeSelection(startTimeSelection || getDefaultStartTimeSelection())

        const repeatPreset = repeatRule._repeatMenuPreset
        if (repeatPreset === 'repeat_daily' && startTimeSelection) {
        } else if (repeatPreset === 'repeat_weekly') {
            next.byweekday = [(startDate || new Date()).getDay()]
        } else if (repeatPreset === 'repeat_monthly') {
            next.bymonthday = (startDate || new Date()).getDate()
        } else if (repeatPreset === 'repeat_custom') {
        }

        next.byhour = hour
        next.byminute = min

        if (!isModify && startDate) {
            const dStartM = moment(startDate).startOf('day').add(hour, 'hours').add(min, 'minutes')

            next.dtstart = dStartM.toISOString()
        }

        setRepeatRule(next)
    }, [repeatRule._repeatMenuPreset, startTimeSelection, startDate, timeZoneCode])

    useEffect(() => {
        setRrule(toRRule(repeatRule)?.rrule || null)
    }, [repeatRule])

    const onSetRepeatPreset = (newPreset: string) => {
        const next = { ...repeatRule }

        if (newPreset === 'repeat_never') {
            next.freq = null
            next._repeatMenuPreset = null
        } else if (newPreset === 'repeat_daily') {
            next.freq = 'daily'
            next._repeatMenuPreset = newPreset
        } else if (newPreset === 'repeat_weekly') {
            next.freq = 'weekly'
            next._repeatMenuPreset = newPreset
        } else if (newPreset === 'repeat_monthly') {
            next.freq = 'monthly'
            next._repeatMenuPreset = newPreset
        } else if (newPreset === 'repeat_custom') {
            if (!next.freq) {
                next.freq = 'daily'
            }
            next._repeatMenuPreset = newPreset
        }

        setRepeatRule(next)
    }

    const onCustomRepeatSelection = (value: string) => {
        setRepeatRule((s) => {
            const next = { ...s }
            if (value === 'daily') {
                next.freq = value
            } else if (value === 'weekly') {
                next.freq = value
                if (!s.byweekday || s.byweekday.length === 0) {
                    next.byweekday = [(startDate || new Date()).getDay()]
                }
            } else if (value === 'monthly') {
                next.freq = value
                if (!s.bymonthday) {
                    next.bymonthday = (startDate || new Date()).getDate()
                }
            }

            return next
        })
    }

    const onSetRepeatInterval = (str: string) => {
        const value = Math.max(parseInt(str) || 1, 0)
        setRepeatRule((s) => {
            return { ...s, interval: value }
        })
    }

    const canCreateRecurringSessions = state.profile?.policy?.canCreateRecurringSessions || false

    const repeatItems = [
        { label: t('Never') || '', value: 'repeat_never' },
        { label: `Daily at ${formatTimeSelection(startTimeSelection || 0)}`, value: 'repeat_daily' },
        { label: `Weekly on ${startDateWeekday}`, value: 'repeat_weekly' },
        { label: `Monthly on the ${startDateDay}`, value: 'repeat_monthly' },
        { label: t('Custom...') || '', value: 'repeat_custom' },
    ]
    const isCustom = repeatRule._repeatMenuPreset === 'repeat_custom'

    return (
        <>
            {isCustom && <div className="w-full border-b border-white/10"></div>}
            <div className={cn('w-full grid grid-cols-[30%_1fr]', !isCustom && 'items-center')}>
                <div className={cn(isCustom && 'pt-2')}>{t('Repeat')}</div>
                <div className="flex items-center flex-wrap gap-2">
                    {canCreateRecurringSessions ? (
                        <SimpleSelect
                            items={repeatItems}
                            value={repeatRule._repeatMenuPreset || 'repeat_never'}
                            onChange={(value: string) => onSetRepeatPreset(value)}
                        />
                    ) : (
                        <Tooltip position={'bottom'} text={'Please upgrade the account to create recurring sessions'}>
                            {/* div needed for the tooltip */}
                            <div>
                                <SimpleSelect
                                    items={[repeatItems[0]]}
                                    value={repeatRule._repeatMenuPreset || 'repeat_never'}
                                    disabled
                                />
                            </div>
                        </Tooltip>
                    )}

                    {isCustom && (
                        <SimpleSelect
                            value={customRepeatSelection}
                            items={[
                                { label: t('Daily') || '', value: 'daily' },
                                { label: t('Weekly') || '', value: 'weekly' },
                                { label: t('Monthly') || '', value: 'monthly' },
                            ]}
                            onChange={(value: string) => onCustomRepeatSelection(value)}
                        ></SimpleSelect>
                    )}

                    {isCustom && (
                        <div className="pl-6 flex flex-wrap gap-2">
                            {/* Repeat - Every */}
                            <div className="flex w-full items-center gap-2">
                                <div>Every</div>
                                <TextInput
                                    className="w-[8ch]"
                                    type="text"
                                    value={repeatRule?.interval?.toString() || '1'}
                                    onChange={onSetRepeatInterval}
                                ></TextInput>
                                <div>
                                    {customRepeatSelection === 'daily' && <div>day(s)</div>}
                                    {customRepeatSelection === 'weekly' && <div>week(s)</div>}
                                    {customRepeatSelection === 'monthly' && <div>month(s)</div>}
                                </div>
                            </div>

                            {/* Repeat - On (weekday selection) */}
                            {customRepeatSelection === 'weekly' && (
                                <div className={'w-full flex gap-2 items-center'}>
                                    {[
                                        { label: 'S', value: 0 },
                                        { label: 'M', value: 1 },
                                        { label: 'T', value: 2 },
                                        { label: 'W', value: 3 },
                                        { label: 'T', value: 4 },
                                        { label: 'F', value: 5 },
                                        { label: 'S', value: 6 },
                                    ].map((day) => (
                                        <button
                                            key={day.value}
                                            onClick={() => {
                                                setRepeatRule((prev) => {
                                                    const newDays = prev.byweekday.includes(day.value)
                                                        ? prev.byweekday.filter((d) => d !== day.value)
                                                        : [...prev.byweekday, day.value]
                                                    return { ...prev, byweekday: newDays }
                                                })
                                            }}
                                            className={`cursor-pointer px-3 py-1 rounded ${
                                                repeatRule.byweekday.includes(day.value)
                                                    ? 'bg-muse-primary-gradient text-black'
                                                    : 'bg-white/5 hover:bg-white/10'
                                            }`}
                                        >
                                            {day.label}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Repeat - On (monthly selection) */}
                            {customRepeatSelection === 'monthly' && (
                                <>
                                    <div className={'w-full flex items-center gap-2'}>
                                        {/* corresponding to `bymonthday` */}
                                        <Checkbox
                                            checked={repeatRule._monthlyRepeatType === 'by_date'}
                                            onChange={() =>
                                                setRepeatRule((s) => ({ ...s, _monthlyRepeatType: 'by_date' }))
                                            }
                                        />
                                        <div>On the</div>
                                        <SimpleSelect
                                            value={(repeatRule.bymonthday || 1).toString()}
                                            onChange={(value: string) => {
                                                setRepeatRule((s) => ({
                                                    ...s,
                                                    bymonthday: parseInt(value),
                                                    _monthlyRepeatType: 'by_date',
                                                }))
                                            }}
                                            items={_.range(1, 32).map((n) => {
                                                return { label: n.toString(), value: n.toString() }
                                            })}
                                        />
                                    </div>
                                    <div className={'w-full flex items-center gap-2'}>
                                        {/* corresponding to `bysetpos` */}
                                        <Checkbox
                                            checked={repeatRule._monthlyRepeatType === 'by_weekday'}
                                            onChange={() =>
                                                setRepeatRule((s) => ({ ...s, _monthlyRepeatType: 'by_weekday' }))
                                            }
                                        />
                                        <div>On the</div>
                                        <SimpleSelect
                                            value={(repeatRule._monthlyRepeatByweekdayNth || 1).toString()}
                                            onChange={(value: string) => {
                                                setRepeatRule((s) => ({
                                                    ...s,
                                                    _monthlyRepeatByweekdayNth: parseInt(value),
                                                    _monthlyRepeatType: 'by_weekday',
                                                }))
                                            }}
                                            items={[
                                                { label: t('First'), value: '1' },
                                                { label: t('Second'), value: '2' },
                                                { label: t('Third'), value: '3' },
                                                { label: t('Fourth'), value: '4' },
                                                { label: t('Last'), value: '-1' },
                                            ]}
                                        />
                                        <SimpleSelect
                                            value={(repeatRule._monthlyRepeatByweekday || 0).toString()}
                                            onChange={(value: string) => {
                                                setRepeatRule((s) => ({
                                                    ...s,
                                                    _monthlyRepeatByweekday: parseInt(value),
                                                    _monthlyRepeatType: 'by_weekday',
                                                }))
                                            }}
                                            items={[
                                                { label: t('Sunday'), value: '0' },
                                                { label: t('Monday'), value: '1' },
                                                { label: t('Tuesday'), value: '2' },
                                                { label: t('Wednesday'), value: '3' },
                                                { label: t('Thursday'), value: '4' },
                                                { label: t('Friday'), value: '5' },
                                                { label: t('Saturday'), value: '6' },
                                            ]}
                                        />
                                    </div>
                                </>
                            )}

                            {/* Repeat - End */}
                            <div className={'w-full flex items-center gap-2'}>
                                <div>{t('End')}</div>
                                <SimpleSelect
                                    value={(() => {
                                        if (repeatRule.until) {
                                            return 'end_on'
                                        }
                                        if (repeatRule.count) {
                                            return 'end_after'
                                        }
                                        return 'end_never'
                                    })()}
                                    onChange={(value) => {
                                        setRepeatRule((s) => {
                                            if (value === 'end_never') {
                                                return { ...s, until: null, count: null }
                                            } else if (value === 'end_on') {
                                                const until = s.until ? s.until : moment().add(6, 'month').toDate()
                                                return { ...s, until, count: null }
                                            } else if (value === 'end_after') {
                                                return { ...s, until: null, count: s.count || 10 }
                                            }
                                            return s
                                        })
                                    }}
                                    items={[
                                        { label: t('Never') || '', value: 'end_never' },
                                        { label: t('On...') || '', value: 'end_on' },
                                        { label: t('After...') || '', value: 'end_after' },
                                    ]}
                                />
                                {repeatRule.until && (
                                    <div className="flex items-center">
                                        <div ref={datePickerContainerRef}>
                                            <MDatePicker
                                                date={repeatRule.until}
                                                closerClickEv={calendarCloserClickEv}
                                                onChange={(date) => {
                                                    if (date) {
                                                        setRepeatRule((s) => {
                                                            return { ...s, until: date }
                                                        })
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}
                                {repeatRule.count && (
                                    <>
                                        <TextInput
                                            className="w-[8ch]"
                                            type="text"
                                            value={repeatRule.count?.toString() || '1'}
                                            onChange={(str: string) => {
                                                const n = parseInt(str)
                                                if (n > 0) {
                                                    setRepeatRule((s) => {
                                                        return { ...s, count: n }
                                                    })
                                                }
                                            }}
                                        />
                                        <div>{t('occurrences')}</div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {rrule && <div>{rrule.toText()}</div>}
                </div>
            </div>
        </>
    )
}
